import { audioAssets } from '../content/audio';
import type { GameSettings } from '../domain/types';

type Theme = 'map' | 'forest';

export class AudioManager {
  #context: AudioContext | null = null;
  #musicGain: GainNode | null = null;
  #sfxGain: GainNode | null = null;
  #ambienceGain: GainNode | null = null;
  #ambienceOscillator: OscillatorNode | null = null;
  #musicElement: HTMLAudioElement | null = null;
  #playbackRequested = false;
  #theme: Theme = 'map';
  #settings: GameSettings;

  constructor(settings: GameSettings) {
    this.#settings = structuredClone(settings);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.#musicElement?.pause();
        void this.#context?.suspend();
      } else if (this.#playbackRequested) {
        void this.#resumeAudio();
      }
    });
  }

  get playing(): boolean {
    return (
      this.#playbackRequested &&
      this.#context?.state === 'running' &&
      this.#musicElement?.paused === false &&
      !this.#settings.muted
    );
  }

  async activate(): Promise<boolean> {
    this.#playbackRequested = true;
    this.#context ??= new AudioContext();
    if (!this.#musicGain || !this.#sfxGain || !this.#ambienceGain) {
      this.#musicGain = this.#context.createGain();
      this.#sfxGain = this.#context.createGain();
      this.#ambienceGain = this.#context.createGain();
      this.#musicGain.connect(this.#context.destination);
      this.#sfxGain.connect(this.#context.destination);
      this.#ambienceGain.connect(this.#context.destination);
      this.#connectBackgroundMusic();
      this.#startAmbiencePlaceholder();
    }
    this.applySettings(this.#settings);
    return this.#resumeAudio();
  }

  deactivate(): void {
    this.#playbackRequested = false;
    this.#musicElement?.pause();
    void this.#context?.suspend();
  }

  applySettings(settings: GameSettings): void {
    this.#settings = structuredClone(settings);
    const now = this.#context?.currentTime ?? 0;
    this.#musicGain?.gain.setTargetAtTime(
      settings.muted ? 0 : settings.musicVolume * 0.7,
      now,
      0.18,
    );
    this.#sfxGain?.gain.setTargetAtTime(settings.muted ? 0 : settings.sfxVolume * 0.16, now, 0.04);
    const ambienceLevel = this.#theme === 'forest' ? 0.018 : 0.004;
    this.#ambienceGain?.gain.setTargetAtTime(
      settings.muted ? 0 : settings.musicVolume * ambienceLevel,
      now,
      0.3,
    );
  }

  setTheme(theme: Theme): void {
    this.#theme = theme;
    if (!this.#context) return;
    const ambienceLevel = theme === 'forest' ? 0.018 : 0.004;
    this.#ambienceGain?.gain.setTargetAtTime(
      this.#settings.muted ? 0 : this.#settings.musicVolume * ambienceLevel,
      this.#context.currentTime,
      0.7,
    );
  }

  playSfx(soundId: string): void {
    if (!this.#context || !this.#sfxGain || this.#settings.muted) return;
    const oscillator = this.#context.createOscillator();
    const gain = this.#context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = soundId === 'item-acquired' ? 659.25 : 392;
    gain.gain.setValueAtTime(0, this.#context.currentTime);
    gain.gain.linearRampToValueAtTime(0.9, this.#context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, this.#context.currentTime + 0.32);
    oscillator.connect(gain).connect(this.#sfxGain);
    oscillator.start();
    oscillator.stop(this.#context.currentTime + 0.34);
  }

  #connectBackgroundMusic(): void {
    if (!this.#context || !this.#musicGain || this.#musicElement) return;
    this.#musicElement = new Audio(audioAssets.backgroundMusic.url);
    this.#musicElement.loop = audioAssets.backgroundMusic.loop;
    this.#musicElement.preload = 'auto';
    this.#musicElement.hidden = true;
    this.#musicElement.dataset.audioId = audioAssets.backgroundMusic.id;
    document.body.append(this.#musicElement);
    this.#context.createMediaElementSource(this.#musicElement).connect(this.#musicGain);
  }

  #startAmbiencePlaceholder(): void {
    if (!this.#context || !this.#ambienceGain) return;
    this.#ambienceOscillator = this.#context.createOscillator();
    this.#ambienceOscillator.type = 'sine';
    this.#ambienceOscillator.frequency.value = 55;
    this.#ambienceOscillator.connect(this.#ambienceGain);
    this.#ambienceOscillator.start();
  }

  async #resumeAudio(): Promise<boolean> {
    if (!this.#context) return false;
    try {
      // play() kaldes før første await, så browserens korte brugeraktivering ikke når at udløbe.
      const playback = this.#musicElement?.play();
      await Promise.all([this.#context.resume(), playback]);
      return this.#context.state === 'running' && this.#musicElement?.paused === false;
    } catch {
      this.#playbackRequested = false;
      return false;
    }
  }
}
